/* eslint-disable max-len */

import React from 'react';
import '@universe/orion/build/output.css'; 
import { BrowserRouter, Switch, Route } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import { OrionTranslationProvider } from '@universe/orion';
import { Mutation } from 'react-apollo';
import config from 'config';
import amplitude from 'amplitude-js';
import { initializeSentry } from 'lib/sentry';
import { initializeGrafanaFaro } from 'lib/faro';
import { ErrorManagerProvider } from 'context/error-manager-context/main';
import { NoPermissionsError } from 'components/utility/NoPermissionsError';
import { ErrorBoundary } from 'components/utility/error-boundary/error-boundary';
import { FliptProvider } from '@flipt-io/flipt-client-react';
import moment from 'moment';
import { asyncComponent } from 'lib/async-component';
import { detectInitialLocale, loadMessages, setLocale, getLocale } from 'lib/locale';
import { DefaultDigitalData } from 'lib/digital-data';
import updateLocaleMutation from 'update-locale.gql';
import LocaleContext from 'context/locale-context';
import Balance from './routes/dashboard/Balance/Balance';
import AddOns from './routes/add-ons';
import AddOnsCreate from './routes/add-ons/create-edit';
import AddOnOrders from './routes/add-ons/orders';
import './app.css';
import '../node_modules/milkyway/semantic.min.css';
import { isBetaUser } from './utils';
import { withOrganization } from './components/organization-select/withOrganization';

// cache value on the window object to survive path changes
window.ccg = /r=CCG/i.test(window.location.search || '');

const Create = asyncComponent(
  () => import(/* webpackChunkName: "routes-create" */ 'src/routes/create/index.js'),
  'Create',
);
const Manage = asyncComponent(
  () => import(/* webpackChunkName: "routes-manage" */ 'src/routes/manage/index.js'),
  'Manage',
);
const EventSelect = asyncComponent(
  () => import(/* webpackChunkName: "routes-create" */ 'src/routes/create/steps/eventSelect.js'),
  'EventSelect',
);
const Reports = asyncComponent(
  () => import(/* webpackChunkName: "routes-reports" */ 'src/routes/reports/index.js'),
  'Reports',
);
const OnsiteReports = asyncComponent(
  () => import(/* webpackChunkName: "routes-onsite-reports" */ 'src/routes/reports/onsite/index.js'),
);

const Fees = asyncComponent(
  () => import(/* webpackChunkName: "routes-fees" */ 'src/routes/dashboard/fees/index.js'),
  'Fees',
);

const Conversations = asyncComponent(
  () => import(/* webpackChunkName: "routes-conversations" */ 'src/routes/conversations/index.js'),
  'Conversations'
);

const ViewerSettings = asyncComponent(
  () => import(/* webpackChunkName: "routes-settings" */ 'src/routes/dashboard/settings/ViewerSettings.js'),
  'Settings',
);

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      locale: null,
      messages: null,
      intervalId: null,
      user: props.user,
    };

    amplitude.getInstance().init(config.amplitude.key);
    initializeSentry(props.user);
    initializeGrafanaFaro();
  }

  async componentDidMount() {
    const locale = detectInitialLocale();
    const messages = await loadMessages(locale);
    await this.setState({ locale, messages });

    moment.locale(locale);

    const intervalId = window.setInterval(async () => {
      const localStorageLocale = getLocale();
      if (localStorageLocale && localStorageLocale !== this.state.locale) {
        const localStorageMessages = await loadMessages(localStorageLocale);
        this.setState({ locale: localStorageLocale, messages: localStorageMessages });
      }
    }, 1000);
    this.setState({ intervalId });
    setLocale(this.state.locale);
  }

  componentDidUpdate(_prevProps, prevState) {
    if (this.state.locale !== prevState.locale) setLocale(this.state.locale);
  }

  componentWillUnmount() {
    clearInterval(this.state.intervalId);
  }

  switchLocale = localeChange => async locale => {
    const messages = await loadMessages(locale);
    this.setState({ locale, messages });
    if (this.state.user) localeChange({ variables: { locale: locale.toUpperCase().replace('-', '_') } });
  };

  render() {
    if (!this.state.messages) return null;

    return (
      <FliptProvider
        namespace={config.flipt.namespace}
        options={config.flipt.options}
      >
        <Mutation mutation={updateLocaleMutation}>
          {localeChange => (
            <LocaleContext.Provider
              value={{
                switchLocale: this.switchLocale(localeChange),
                user: this.props.user,
                locale: this.state.locale,
              }}
            >
              <DefaultDigitalData />
              <IntlProvider locale={this.state.locale} messages={this.state.messages}>
                <OrionTranslationProvider locale= {this.state.locale}>
                  <BrowserRouter>
                    <ErrorBoundary>
                      <ErrorManagerProvider>
                        <Switch>
                          <Route
                            exact
                            path="/create"
                            render={props =>
                              isBetaUser(this.props.user) ? <EventSelect {...props} /> : <NoPermissionsError />
                            }
                          />
                          <Route
                            exact
                            path="/create/:listingType/:step?"
                            render={props => {
                              return(isBetaUser(this.props.user) ? <Create {...props} /> : <NoPermissionsError />);
                            }}
                          />
                          <Route exact path="/host/events/:id/:dest/:step?" component={Manage} />
                          <Route exact path="/host/dashboard/balance/:currency" render={data => <Balance currency={data.match.params.currency} user={this.props.user} /> } />
                          <Route exact path={["/host/dashboard/new", "/dashboard"]} component={Reports} />
                          <Route exact path="/host/dashboard/onsite" component={OnsiteReports} />
                          <Route exact path="/dashboard/add-ons" component={AddOns} />
                          <Route exact path="/dashboard/add-ons/create"  component={AddOnsCreate} />
                          <Route exact path="/dashboard/add-ons/edit/:id" component={AddOnsCreate} />
                          <Route exact path="/dashboard/add-ons/orders" component={AddOnOrders} />
                          <Route exact path="/host/fees" component={Fees} />

                          <Route exact path="/dashboard/conversations/withUser/:userId?" component={Conversations} />
                          <Route exact path="/dashboard/conversations/:conversationId?" component={Conversations} />
                          <Route exact path="/host/dashboard/conversations/withUser/:userId?" component={Conversations} />
                          <Route exact path="/host/dashboard/conversations/:conversationId?" component={Conversations} />

                          <Route exact path="/host/dashboard/settings" component={ViewerSettings} />
                          <Route exact path="/host/dashboard/settings/profile" component={ViewerSettings} />
                          <Route exact path="/host/dashboard/settings/billing" component={ViewerSettings} />
                          <Route exact path="/host/dashboard/settings/email" component={ViewerSettings} />
                          <Route exact path="/host/dashboard/organizations" component={ViewerSettings} />
                          <Route exact path="/host/dashboard/payment-preferences" component={ViewerSettings} />
                          <Route exact path="/host/dashboard/settings/stripe-devices" component={ViewerSettings} />
                          <Route exact path="/dashboard/settings" component={ViewerSettings} />
                          <Route exact path="/dashboard/settings/profile" component={ViewerSettings} />
                          <Route exact path="/dashboard/settings/billing" component={ViewerSettings} />
                          <Route exact path="/dashboard/settings/email" component={ViewerSettings} />
                          <Route exact path="/dashboard/organizations" component={ViewerSettings} />
                          <Route exact path="/dashboard/payment-preferences" component={ViewerSettings} />
                          <Route exact path="/dashboard/settings/stripe-devices" component={ViewerSettings} />
                        </Switch>
                      </ErrorManagerProvider>
                    </ErrorBoundary>
                  </BrowserRouter>
                </OrionTranslationProvider>
              </IntlProvider>
            </LocaleContext.Provider>
          )}
        </Mutation>
      </FliptProvider>
    );
  }
}

export default withOrganization(App);
